// router.js — client-side SPA router. Replaces the old app.js router. Features:
// pattern params, auth guards (redirect to /login), per-route lazy import() for
// code-splitting, page-cleanup lifecycle, a loading state during chunk fetch,
// and a chunk-load error boundary with Retry (so a stale [hash] after deploy
// never strands a route — spec risk #9).
//
// A route: { path, name, tag, load, auth }. `load()` dynamically imports the
// page module (which registers `tag` via define()); the router then mounts
// <tag> with the matched params as attributes.

import { state, runPageCleanup, isAuthed } from './state.js';

// Compile '/avatar/:id/chat' -> regex + param names.
export function compile(pattern) {
  const keys = [];
  const body = pattern.replace(/\/+$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape literals
  const src = '^' + body.replace(/:([A-Za-z0-9_]+)/g, (_m, k) => { keys.push(k); return '([^/]+)'; }) + '/?$';
  return { rx: new RegExp(src), keys };
}

// Test helper: returns params object or null.
export function matchPath(pattern, path) {
  const { rx, keys } = compile(pattern);
  const m = rx.exec(path);
  if (!m) return null;
  const params = {};
  keys.forEach((k, i) => { params[k] = decodeURIComponent(m[i + 1]); });
  return params;
}

function loadingShell(outlet) {
  outlet.replaceChildren();
  const wrap = document.createElement('div');
  wrap.className = 'pg-boot';
  wrap.setAttribute('role', 'status');
  wrap.setAttribute('aria-live', 'polite');
  wrap.innerHTML = '<div class="pg-boot__spinner" aria-hidden="true"></div><div class="pg-boot__label">Loading…</div>';
  outlet.appendChild(wrap);
}

function chunkError(outlet, retry) {
  outlet.replaceChildren();
  const wrap = document.createElement('div');
  wrap.className = 'pg-boot';
  wrap.setAttribute('role', 'alert');
  const msg = document.createElement('p');
  msg.textContent = "Couldn't load this page.";
  const btn = document.createElement('button');
  btn.textContent = 'Retry';
  btn.addEventListener('click', retry);
  wrap.append(msg, btn);
  outlet.appendChild(wrap);
}

export function createRouter({ routes, outlet, notFound }) {
  const compiled = routes.map((r) => ({ ...r, _c: compile(r.path) }));

  function find(pathname) {
    for (const r of compiled) {
      const m = r._c.rx.exec(pathname);
      if (m) {
        const params = {};
        r._c.keys.forEach((k, i) => { params[k] = decodeURIComponent(m[i + 1]); });
        return { route: r, params };
      }
    }
    return notFound ? { route: notFound, params: {} } : null;
  }

  async function render() {
    const pathname = location.pathname;
    runPageCleanup();
    const hit = find(pathname);
    if (!hit) return;
    const { route, params } = hit;

    if (route.auth && !isAuthed()) {
      navigate('/login?next=' + encodeURIComponent(pathname + location.search), { replace: true });
      return;
    }

    loadingShell(outlet);
    try {
      if (route.load) await route.load(); // registers the custom element (side-effect)
    } catch (err) {
      console.error('[router] chunk load failed', err);
      chunkError(outlet, render);
      return;
    }

    state.route.value = { path: pathname, params, name: route.name, query: location.search };
    const el = document.createElement(route.tag);
    for (const [k, v] of Object.entries(params)) el.setAttribute(k, v);
    outlet.replaceChildren(el);
  }

  function navigate(to, { replace = false } = {}) {
    if (replace) history.replaceState({}, '', to);
    else history.pushState({}, '', to);
    render();
  }

  function onClick(e) {
    const a = e.target.closest && e.target.closest('a[href^="/"]');
    if (!a || a.target || a.hasAttribute('download') || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(a.getAttribute('href'));
  }

  window.addEventListener('popstate', render);
  document.addEventListener('click', onClick);

  return {
    start: render,
    navigate,
    destroy() { window.removeEventListener('popstate', render); document.removeEventListener('click', onClick); },
  };
}
